(() => {
  'use strict';

  const VERSION = 3;
  const ROOT_SELECTOR = '[data-wub-newsletter-popup]';
  const LOCK_CLASS = 'wub-newsletter-popup-open';
  const DAY_MS = 24 * 60 * 60 * 1000;
  const STORAGE_KEYS = Object.freeze({
    closed: 'wub_newsletter_popup_closed_at_v1',
    subscribed: 'wub_newsletter_popup_subscribed_at_v1',
    session: 'wub_newsletter_popup_seen_session_v1',
    submitting: 'wub_newsletter_popup_submitting_v1',
    launcherDismissed: 'wub_popup_launcher_dismissed_session_v1'
  });
  const instances = new WeakMap();
  const liveInstances = new Set();

  function safeGet(storage, key) {
    try { return storage.getItem(key); } catch (_) { return null; }
  }

  function safeSet(storage, key, value) {
    try { storage.setItem(key, value); } catch (_) {}
  }

  function safeRemove(storage, key) {
    try { storage.removeItem(key); } catch (_) {}
  }

  function isWithinDays(timestamp, days) {
    if (!timestamp || Number(days) <= 0) return false;
    const savedAt = Number(timestamp);
    if (!Number.isFinite(savedAt)) return false;
    return Date.now() - savedAt < Number(days) * DAY_MS;
  }

  function visibleFocusable(container) {
    if (!container) return [];
    return Array.from(container.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])'
    )).filter((node) => !node.hidden && node.getAttribute('aria-hidden') !== 'true' && node.getClientRects().length > 0);
  }

  function otherNativePopupIsOpen(excludedRoot) {
    return Array.from(document.querySelectorAll(ROOT_SELECTOR)).some((root) => (
      root !== excludedRoot &&
      !root.hidden &&
      (root.dataset.state === 'open' || root.dataset.state === 'opening')
    ));
  }

  function anotherWubOverlayIsOpen() {
    const body = document.body;
    if (!body) return false;

    if (
      body.classList.contains('wub-commerce-locked') ||
      body.classList.contains('wub-shell-locked') ||
      body.classList.contains('wub-header--locked') ||
      body.classList.contains('wub-filters-lock') ||
      body.classList.contains('wub-size-lock')
    ) return true;

    return Boolean(document.querySelector([
      '#WubCartDrawer.is-open',
      '#Search-Drawer.active',
      '#Search-Drawer[aria-hidden="false"]',
      '[data-wub-header].is-menu-open',
      '[data-wub-header].is-search-open',
      '[data-wub-header].is-drawer-open',
      '[data-wub-collection][data-filters-open="true"]',
      '[data-wub-size-sheet]:not([hidden])'
    ].join(',')));
  }

  class WubNewsletterPopup {
    constructor(root) {
      this.root = root;
      this.config = this.readConfig();
      this.dialog = root.querySelector('[data-wub-popup-dialog]');
      this.form = root.querySelector('form.wub-popup-newsletter-form');
      this.launcher = document.querySelector(`[data-wub-popup-launcher-wrap="${this.config.sectionId || ''}"]`);
      this.launcherButton = this.launcher?.querySelector('[data-wub-newsletter-popup-launcher]') || null;
      this.launcherDismiss = this.launcher?.querySelector('[data-wub-newsletter-popup-launcher-dismiss]') || null;
      this.formContent = root.querySelector('[data-wub-form-content]');
      this.successContent = root.querySelector('[data-wub-success-content]');
      this.errorContent = root.querySelector('[data-wub-error-content]');
      this.serverSuccess = root.querySelector('[data-wub-server-success]');
      this.serverError = root.querySelector('[data-wub-server-error]');
      this.startedAt = Date.now();
      this.shownThisPage = false;
      this.launcherShouldBeVisible = false;
      this.mobileScrollReached = false;
      this.previousFocus = null;
      this.hideTimer = 0;
      this.timers = new Set();
      this.cleanup = [];
      this.controller = typeof AbortController === 'function' ? new AbortController() : null;
      this.mediaQuery = window.matchMedia('(max-width: 749px)');

      if (!this.dialog || !this.form || !this.launcher) return;

      root.dataset.wubPopupInitialized = 'true';
      liveInstances.add(this);
      this.bind();
      this.initialiseState();
    }

    readConfig() {
      const node = this.root.querySelector('[data-wub-popup-config]');
      if (!node) return {};
      try { return JSON.parse(node.textContent || '{}'); } catch (_) { return {}; }
    }

    eventOptions(extra = {}) {
      return this.controller ? { ...extra, signal: this.controller.signal } : extra;
    }

    on(target, name, handler, options = {}) {
      if (!target?.addEventListener) return;
      target.addEventListener(name, handler, this.eventOptions(options));
      if (!this.controller) this.cleanup.push(() => target.removeEventListener(name, handler, options));
    }

    schedule(callback, seconds) {
      const delay = Math.max(0, Number(seconds) * 1000);
      const timer = window.setTimeout(() => {
        this.timers.delete(timer);
        callback();
      }, delay);
      this.timers.add(timer);
      return timer;
    }

    isActive() {
      return !this.root.hidden && (this.root.dataset.state === 'open' || this.root.dataset.state === 'opening');
    }

    hasSignupSuppression() {
      return isWithinDays(safeGet(localStorage, STORAGE_KEYS.subscribed), this.config.hideAfterSignupDays);
    }

    hasCloseSuppression() {
      return isWithinDays(safeGet(localStorage, STORAGE_KEYS.closed), this.config.hideAfterCloseDays);
    }

    hasSessionSuppression() {
      return Boolean(this.config.oncePerSession && safeGet(sessionStorage, STORAGE_KEYS.session) === '1');
    }

    launcherDismissed() {
      return safeGet(sessionStorage, STORAGE_KEYS.launcherDismissed) === '1';
    }

    isSuppressed() {
      if (this.config.designMode) return false;
      return this.hasSessionSuppression() || this.hasSignupSuppression() || this.hasCloseSuppression();
    }

    isMobileViewport() {
      return this.mediaQuery.matches;
    }

    launcherEnabledForViewport() {
      return this.isMobileViewport()
        ? Boolean(this.config.launcherMobileEnabled)
        : Boolean(this.config.launcherDesktopEnabled);
    }

    syncLauncherVisibility() {
      if (!this.launcher) return;
      const canShow = Boolean(
        this.launcherShouldBeVisible &&
        this.launcherEnabledForViewport() &&
        !this.isActive() &&
        !this.launcherDismissed() &&
        (this.config.designMode || !this.hasSignupSuppression())
      );
      this.launcher.hidden = !canShow;
    }

    showLauncher() {
      this.launcherShouldBeVisible = true;
      this.syncLauncherVisibility();
    }

    hideLauncher() {
      this.launcherShouldBeVisible = false;
      if (this.launcher) this.launcher.hidden = true;
    }

    lockPage() {
      document.documentElement.classList.add(LOCK_CLASS);
      document.body?.classList.add(LOCK_CLASS);
    }

    unlockPage() {
      if (otherNativePopupIsOpen(this.root)) return;
      document.documentElement.classList.remove(LOCK_CLASS);
      document.body?.classList.remove(LOCK_CLASS);
    }

    dispatch(name, detail = {}) {
      document.dispatchEvent(new CustomEvent(name, {
        detail: { sectionId: this.config.sectionId || '', ...detail }
      }));
    }

    initialFocusTarget() {
      const email = this.dialog.querySelector('input[type="email"]');
      if (email && !email.hidden && email.getClientRects().length > 0) return email;
      return visibleFocusable(this.dialog)[0] || this.dialog;
    }

    open(options = {}) {
      const force = options.force === true;
      const reason = options.reason || 'runtime';
      const ignoreOtherOverlays = options.ignoreOtherOverlays === true;

      if (this.isActive()) return false;
      if (!ignoreOtherOverlays && anotherWubOverlayIsOpen()) return false;
      if (this.shownThisPage && !force) return false;
      if (this.isSuppressed() && !force) return false;

      this.shownThisPage = true;
      this.hideLauncher();

      if (this.config.oncePerSession && !force) safeSet(sessionStorage, STORAGE_KEYS.session, '1');

      if (this.hideTimer) {
        window.clearTimeout(this.hideTimer);
        this.hideTimer = 0;
      }

      this.previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      this.root.hidden = false;
      this.root.setAttribute('aria-hidden', 'false');
      this.root.dataset.state = 'opening';
      this.lockPage();

      window.requestAnimationFrame(() => {
        if (this.root.hidden || this.root.dataset.state !== 'opening') return;
        this.root.dataset.state = 'open';
        const target = this.initialFocusTarget();
        window.requestAnimationFrame(() => target?.focus?.({ preventScroll: true }));
        this.dispatch('wub:newsletter-popup-opened', { reason });
      });

      return true;
    }

    close(options = {}) {
      if (!this.isActive()) return false;

      const reason = options.reason || 'close';
      const remember = options.remember !== false;
      const restoreFocus = options.restoreFocus !== false;
      const showLauncher = options.showLauncher !== false;

      this.root.dataset.state = 'closed';
      this.root.setAttribute('aria-hidden', 'true');
      this.unlockPage();

      if (remember && !this.config.designMode && !this.hasSignupSuppression()) {
        safeSet(localStorage, STORAGE_KEYS.closed, String(Date.now()));
      }

      if (this.hasSignupSuppression() || !showLauncher) this.hideLauncher();
      else this.showLauncher();

      if (this.hideTimer) window.clearTimeout(this.hideTimer);
      this.hideTimer = window.setTimeout(() => {
        this.hideTimer = 0;
        if (this.root.dataset.state === 'closed') this.root.hidden = true;
      }, 220);

      if (
        restoreFocus &&
        this.previousFocus &&
        this.previousFocus !== document.body &&
        this.previousFocus.isConnected &&
        typeof this.previousFocus.focus === 'function'
      ) {
        window.requestAnimationFrame(() => this.previousFocus.focus({ preventScroll: true }));
      }

      this.dispatch('wub:newsletter-popup-closed', { reason });
      return true;
    }

    closeForCompetingOverlay(reason) {
      if (!this.isActive()) return;
      this.close({ reason, remember: false, restoreFocus: false, showLauncher: false });
    }

    secondsSinceStart() {
      return (Date.now() - this.startedAt) / 1000;
    }

    minimumTimeReached(seconds) {
      return this.secondsSinceStart() >= Number(seconds);
    }

    buildReturnTo() {
      const url = new URL(window.location.href);
      url.searchParams.delete('customer_posted');
      url.searchParams.delete('contact_posted');
      url.searchParams.delete('wub-signup');
      url.searchParams.set('wub-signup', '1');
      url.hash = '';
      return url.pathname + url.search;
    }

    updateReturnTo() {
      const input = this.form.querySelector('[data-wub-return-to]');
      if (input) input.value = this.buildReturnTo();
    }

    isOwnSubmit() {
      const url = new URL(window.location.href);
      return url.searchParams.get('wub-signup') === '1' || safeGet(sessionStorage, STORAGE_KEYS.submitting) === '1';
    }

    cleanSubmissionUrl() {
      const url = new URL(window.location.href);
      let changed = false;

      ['wub-signup', 'customer_posted', 'contact_posted'].forEach((parameter) => {
        if (!url.searchParams.has(parameter)) return;
        url.searchParams.delete(parameter);
        changed = true;
      });

      if (url.hash === '#contact_form' || url.hash === '#newsletter-form') {
        url.hash = '';
        changed = true;
      }

      if (changed) window.history.replaceState({}, document.title, url.pathname + url.search + url.hash);
    }

    showSuccessState() {
      if (this.formContent) this.formContent.hidden = true;
      if (this.successContent) this.successContent.hidden = false;
    }

    showErrorState() {
      if (this.errorContent) this.errorContent.hidden = false;
    }

    handleSubmissionResult() {
      if (!this.isOwnSubmit()) return false;

      if (this.serverSuccess) {
        safeSet(localStorage, STORAGE_KEYS.subscribed, String(Date.now()));
        safeRemove(localStorage, STORAGE_KEYS.closed);
        safeSet(sessionStorage, STORAGE_KEYS.session, '1');
        safeRemove(sessionStorage, STORAGE_KEYS.submitting);
        this.hideLauncher();
        this.showSuccessState();
        this.open({ force: true, reason: 'server-success', ignoreOtherOverlays: true });
        this.cleanSubmissionUrl();
        return true;
      }

      if (this.serverError) {
        safeRemove(sessionStorage, STORAGE_KEYS.submitting);
        this.showErrorState();
        this.open({ force: true, reason: 'server-error', ignoreOtherOverlays: true });
        this.cleanSubmissionUrl();
        return true;
      }

      return false;
    }

    getScrollPercent() {
      const documentElement = document.documentElement;
      const scrollableHeight = documentElement.scrollHeight - window.innerHeight;
      if (scrollableHeight <= 0) return 0;
      return Math.min(100, Math.max(0, (window.scrollY / scrollableHeight) * 100));
    }

    mobileScrollRequirementReached() {
      const absoluteScroll = window.scrollY || document.documentElement.scrollTop || 0;
      return this.getScrollPercent() >= Number(this.config.mobileScrollPercent) && absoluteScroll >= Number(this.config.mobileMinScrollPx);
    }

    setupDesktopTriggers() {
      const minTime = Number(this.config.desktopMinTime);

      if (this.config.desktopTrigger === 'exit' || this.config.desktopTrigger === 'exit_fallback') {
        this.on(document, 'mouseout', (event) => {
          const topExit = event.clientY <= Number(this.config.exitThreshold) && !event.relatedTarget;
          if (topExit && this.minimumTimeReached(minTime)) this.open({ reason: 'desktop-exit' });
        });
      }

      if (this.config.desktopTrigger === 'delay') {
        this.schedule(() => this.open({ reason: 'desktop-delay' }), Math.max(Number(this.config.desktopDelay), minTime));
      }

      if (this.config.desktopTrigger === 'exit_fallback') {
        this.schedule(() => this.open({ reason: 'desktop-fallback' }), Math.max(Number(this.config.desktopFallbackDelay), minTime));
      }
    }

    setupMobileTriggers() {
      const minTime = Number(this.config.mobileMinTime);

      if (this.config.mobileTrigger === 'scroll' || this.config.mobileTrigger === 'scroll_fallback') {
        const scrollHandler = () => {
          if (!this.mobileScrollRequirementReached()) return;
          this.mobileScrollReached = true;
          if (this.minimumTimeReached(minTime)) this.open({ reason: 'mobile-scroll' });
        };

        this.on(window, 'scroll', scrollHandler, { passive: true });
        scrollHandler();

        if (minTime > 0) {
          this.schedule(() => {
            if (this.mobileScrollReached) this.open({ reason: 'mobile-scroll-min-time' });
          }, minTime);
        }
      }

      if (this.config.mobileTrigger === 'delay') {
        this.schedule(() => this.open({ reason: 'mobile-delay' }), Math.max(Number(this.config.mobileDelay), minTime));
      }

      if (this.config.mobileTrigger === 'scroll_fallback') {
        this.schedule(() => this.open({ reason: 'mobile-fallback' }), Math.max(Number(this.config.mobileFallbackDelay), minTime));
      }
    }

    trapFocus(event) {
      if (event.key !== 'Tab' || !this.isActive()) return;
      const items = visibleFocusable(this.dialog);

      if (!items.length) {
        event.preventDefault();
        this.dialog.focus({ preventScroll: true });
        return;
      }

      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;

      if (!this.dialog.contains(active)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus({ preventScroll: true });
      } else if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus({ preventScroll: true });
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus({ preventScroll: true });
      }
    }

    bind() {
      this.on(this.form, 'submit', () => {
        this.updateReturnTo();
        safeSet(sessionStorage, STORAGE_KEYS.submitting, '1');
      });

      this.on(this.launcherButton, 'click', (event) => {
        event.preventDefault();
        this.open({ force: true, reason: 'launcher' });
      });

      this.on(this.launcherDismiss, 'click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        safeSet(sessionStorage, STORAGE_KEYS.launcherDismissed, '1');
        this.hideLauncher();
      });

      this.on(this.root, 'click', (event) => {
        const closeControl = event.target.closest?.('[data-wub-popup-close]');
        if (!closeControl || !this.root.contains(closeControl)) return;
        event.preventDefault();
        this.close({ reason: closeControl.dataset.wubPopupClose || 'close-control' });
      });

      this.on(document, 'keydown', (event) => {
        if (!this.isActive()) return;
        if (event.key === 'Escape') {
          event.preventDefault();
          this.close({ reason: 'escape' });
          return;
        }
        this.trapFocus(event);
      }, { capture: true });

      this.on(document, 'wub:cart-drawer-opened', () => this.closeForCompetingOverlay('cart-opened'));
      this.on(document, 'wub:newsletter-popup-request-close', () => this.closeForCompetingOverlay('external-request'));

      this.on(document, 'click', (event) => {
        const trigger = event.target.closest?.([
          '[data-wub-cart-open]',
          '[data-wub-search-open]',
          '[data-wub-menu-open]',
          '[data-wub-filter-open]',
          '[data-wub-size-sheet-open]',
          '[data-wub-sticky-action="filter"]'
        ].join(','));
        if (trigger) this.closeForCompetingOverlay('wub-overlay-trigger');
      }, { capture: true });

      const viewportChange = () => this.syncLauncherVisibility();
      if (typeof this.mediaQuery.addEventListener === 'function') {
        this.on(this.mediaQuery, 'change', viewportChange);
      } else if (typeof this.mediaQuery.addListener === 'function') {
        this.mediaQuery.addListener(viewportChange);
        this.cleanup.push(() => this.mediaQuery.removeListener?.(viewportChange));
      }
    }

    initialiseState() {
      this.root.hidden = true;
      this.root.dataset.state = 'closed';
      this.root.setAttribute('aria-hidden', 'true');
      this.launcher.hidden = true;
      this.unlockPage();

      if (this.handleSubmissionResult()) return;

      if (this.config.designMode) {
        this.schedule(() => this.open({ force: true, reason: 'design-mode' }), 0.3);
        return;
      }

      if (this.hasSignupSuppression()) {
        this.hideLauncher();
        return;
      }

      if (this.isSuppressed()) {
        this.showLauncher();
        return;
      }

      if (this.isMobileViewport()) {
        if (this.config.mobileTrigger === 'disabled') {
          this.showLauncher();
          return;
        }
        this.setupMobileTriggers();
        return;
      }

      if (this.config.desktopTrigger === 'disabled') {
        this.showLauncher();
        return;
      }

      this.setupDesktopTriggers();
    }

    destroy() {
      if (this.controller) this.controller.abort();
      this.cleanup.splice(0).forEach((fn) => {
        try { fn(); } catch (_) {}
      });
      this.timers.forEach((timer) => window.clearTimeout(timer));
      this.timers.clear();
      if (this.hideTimer) window.clearTimeout(this.hideTimer);
      this.root.hidden = true;
      this.root.dataset.state = 'closed';
      this.root.setAttribute('aria-hidden', 'true');
      this.unlockPage();
      this.root.dataset.wubPopupInitialized = 'false';
      liveInstances.delete(this);
    }
  }

  function create(root) {
    if (!(root instanceof Element) || instances.has(root)) return;
    const instance = new WubNewsletterPopup(root);
    instances.set(root, instance);
  }

  function init(scope = document) {
    if (scope instanceof Element && scope.matches(ROOT_SELECTOR)) create(scope);
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach(create);
  }

  function unload(scope) {
    if (!(scope instanceof Element)) return;
    const roots = [];
    if (scope.matches(ROOT_SELECTOR)) roots.push(scope);
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach((root) => roots.push(root));
    roots.forEach((root) => {
      instances.get(root)?.destroy();
      instances.delete(root);
    });
  }

  function closeAll(options = {}) {
    liveInstances.forEach((instance) => instance.close(options));
  }

  if (window.WUBNewsletterPopup?.version === VERSION) {
    window.WUBNewsletterPopup.init(document);
    return;
  }

  window.WUBNewsletterPopup = Object.freeze({ version: VERSION, init, closeAll });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => unload(event.target));
  window.addEventListener('pagehide', () => {
    document.documentElement.classList.remove(LOCK_CLASS);
    document.body?.classList.remove(LOCK_CLASS);
  });
})();
